import {
  type LocalStorageClient,
  localStorageClient,
} from 'clients/local-storage'
import type { AskYogiConnector } from 'features/ask-yogi/service/types'

export interface AskYogiToolPreferences {
  webSearchEnabled: boolean
  selectedConnectors: AskYogiConnector[]
}

const GLOBAL_STORAGE_KEY = 'ask-yogi-tools:global'
const CONVERSATION_STORAGE_KEY_PREFIX = 'ask-yogi-tools:conversation:'
const CONNECTORS = new Set<AskYogiConnector>(['asana', 'monday', 'shopify'])

export const emptyAskYogiToolPreferences = (): AskYogiToolPreferences => ({
  webSearchEnabled: false,
  selectedConnectors: [],
})

const normalizePreferences = (value: unknown): AskYogiToolPreferences => {
  if (typeof value !== 'object' || value === null) {
    return emptyAskYogiToolPreferences()
  }
  const candidate = value as Partial<AskYogiToolPreferences>
  const selectedConnectors = Array.isArray(candidate.selectedConnectors)
    ? candidate.selectedConnectors.filter(
        (connector): connector is AskYogiConnector =>
          typeof connector === 'string' &&
          CONNECTORS.has(connector as AskYogiConnector),
      )
    : []

  return {
    webSearchEnabled: candidate.webSearchEnabled === true,
    selectedConnectors: Array.from(new Set(selectedConnectors)),
  }
}

const conversationKey = (conversationId: string): string =>
  `${CONVERSATION_STORAGE_KEY_PREFIX}${conversationId}`

export class AskYogiToolPreferencesService {
  constructor(private readonly storageClient: LocalStorageClient) {}

  getGlobalPreferences(): AskYogiToolPreferences {
    return normalizePreferences(
      this.storageClient.getJson(
        GLOBAL_STORAGE_KEY,
        emptyAskYogiToolPreferences(),
      ),
    )
  }

  setGlobalPreferences(preferences: AskYogiToolPreferences): void {
    this.storageClient.setJson(
      GLOBAL_STORAGE_KEY,
      normalizePreferences(preferences),
    )
  }

  getConversationPreferences(
    conversationId: string,
  ): AskYogiToolPreferences | undefined {
    const preferences = this.storageClient.getOptionalJson<unknown>(
      conversationKey(conversationId),
    )
    return preferences ? normalizePreferences(preferences) : undefined
  }

  setConversationPreferences(
    conversationId: string,
    preferences: AskYogiToolPreferences,
  ): void {
    this.storageClient.setJson(
      conversationKey(conversationId),
      normalizePreferences(preferences),
    )
  }
}

export const askYogiToolPreferencesService = new AskYogiToolPreferencesService(
  localStorageClient,
)
